import { Text, TextProps } from '@chakra-ui/react'
import * as React from 'react'
import {
  FieldError,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
  UseFormStateReturn,
} from 'react-hook-form'
import { useColor } from '../../../theme'

const InnerLabel: React.FC<
  TextProps & {
    isInvalid: boolean
    label: string
    isDisabled?: boolean
  }
> = ({ isInvalid, label, isDisabled, ...rest }) => {
  const textColor = useColor({ nuance: 2, palette: 'text' })

  return (
    <Text
      as="label"
      color={textColor}
      data-testid={
        isInvalid
          ? 'use-form-validation-hook-label-invalid'
          : 'use-form-validation-hook-label'
      }
      display="inline-block"
      fontSize="sm"
      marginBottom="0.3rem"
      opacity={isDisabled ? 0.25 : 1}
      {...rest}>
      {label}
    </Text>
  )
}

export interface IFormValidationProps {
  isInvalid?: boolean
  Label?: React.FC<TextProps>
  errorMessage?: string
  isDisabled?: boolean
}

export interface ISelectOption { label: string; value: string }

type TuseFormValidationArgs<TFormData extends FieldValues> = {
  formState: UseFormStateReturn<TFormData>
  register: UseFormRegister<TFormData>
}

export type TValidateType<TFormData extends FieldValues> = ({
  name,
  isOptional,
  optionalMessage,
  isDisabled,
  label,
}: Omit<RegisterOptions, 'required'> & {
  isOptional?: boolean
  isDisabled?: boolean
  label?: string
  optionalMessage?: string
  name: Path<TFormData>
}) => IFormValidationProps & ReturnType<UseFormRegister<TFormData>>

export type TuseFormValidationReturn<TFormData extends FieldValues> = {
  validate: TValidateType<TFormData>
} & { hasErrors: boolean }

/**
 * useFormValidation is a hook that enables you to inject validation logic into
 * your form components
 *
 * In a component, the implementation of the hook should first call useForm
 * from react-hook-form:
 *
 * ```tsx
 * const { formState, register } = useForm<TFormData>()
 * ```
 *
 * Then, you can use the `validate` function to inject validation logic:
 *
 * ```tsx
 * const { validate } = useFormValidation(formState, register)
 *
 * return (
 *   <Input
 *     {...validate({
 *        isOptional: true,
 *        label: 'My component',
 *        name: 'my-component-name',
 *     })}
 *   />
 * )
 * ```
 */
export const useFormValidation = <TFormData extends FieldValues>({
  formState: { errors },
  register,
}: TuseFormValidationArgs<TFormData>): TuseFormValidationReturn<TFormData> => {
  const isInvalid = (name: Path<TFormData>) => {
    return typeof errors[name] !== 'undefined'
  }

  return {
    validate: ({
      isOptional = false,
      label,
      name,
      isDisabled,
      optionalMessage,
      ...rest
    }) => ({
      ...register(name, { required: !isOptional, ...rest }),
      ...(typeof label !== 'undefined'
        ? {
          // eslint-disable-next-line react/display-name
          Label: ({ ...rest }) => (
            <InnerLabel
              isDisabled={isDisabled}
              isInvalid={isInvalid(name)}
              label={`${label} ${isOptional && optionalMessage ? `${optionalMessage}` : ''
                }`}
              {...rest}
            />
          ),
        }
        : {}),
      isInvalid: isInvalid(name),
      errorMessage:
        (errors[name] as FieldError | undefined)?.message ?? undefined,
      isDisabled: isDisabled,
    }),
    hasErrors: Object.keys(errors).length > 0,
  }
}
